import React, { useState, useEffect } from "react";
import { TextField, MenuItem, Button, Paper, Typography, Snackbar, Grid, CircularProgress, Card, CardContent, Box, Chip, Avatar, Tooltip } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import DataTable from "react-data-table-component";
import { API_POST } from "../../services/api.service";
import { SUPPORT_DEV_FIELDS } from "./supportDevFields";
import { getOrderedFilterableFields, getDefaultFilterValues, SUPPORT_DEV_FILTER_CONFIG } from "./supportDevFilterConfig";
import { EditAttributesOutlined, Settings, Map, TrendingUp, Pending, CheckCircle, Category, List, Delete as DeleteIcon, Apps, Update, Timeline } from "@material-ui/icons";
import SupportDevConfigPage from "./SupportDevConfigPage";
import SupportDevMappingPopup from "./SupportDevMappingPopup";
import CreateTicketPage from "./CreateTicketPage";
import EditTicketPage from "./EditTicketPage";
import { getTeamDetails } from "../common/helper";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Tabs, Tab } from "@material-ui/core";
import GetAppOutlinedIcon from "@material-ui/icons/GetAppOutlined";


const SupportDevFilter = ({ filterFields, filterValues, setFilterValues, onClear, onFilterChange }) => (
  <Card style={SUPPORT_DEV_FILTER_CONFIG.styling.filterCardStyle}>
    <CardContent>
      <Typography variant="h6" style={{ marginBottom: 16, ...SUPPORT_DEV_FILTER_CONFIG.styling.filterLabelStyle }}>
        {SUPPORT_DEV_FILTER_CONFIG.filterDisplay.filterSectionTitle}
      </Typography>
      <Grid container spacing={SUPPORT_DEV_FILTER_CONFIG.styling.filterGridSpacing}>
        {filterFields.map(field => (
          <Grid item xs={12} sm={6} md={4} key={field.key}>
            {field.type === "autocomplete" ? (
              <Autocomplete
                options={field.options || []}
                value={filterValues[field.key] || null}
                onChange={(event, newValue) => onFilterChange(field.key, newValue || "")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={field.label}
                    variant="outlined"
                    placeholder="All"
                    style={SUPPORT_DEV_FILTER_CONFIG.styling.filterFieldStyle}
                  />
                )}
                freeSolo
                autoComplete
                includeInputInList
                filterSelectedOptions
              />
            ) : field.type === "select" ? (
              <TextField
                select
                label={field.label}
                value={filterValues[field.key] || ""}
                onChange={e => onFilterChange(field.key, e.target.value)}
                fullWidth
                variant="outlined"
                style={SUPPORT_DEV_FILTER_CONFIG.styling.filterFieldStyle}
              >
                <MenuItem value="">All</MenuItem>
                {field.options.map(opt => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </TextField>
            ) : field.type === "date" ? (
              <TextField
                label={field.label}
                type="date"
                value={filterValues[field.key] || ""}
                onChange={e => onFilterChange(field.key, e.target.value)}
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                style={SUPPORT_DEV_FILTER_CONFIG.styling.filterFieldStyle}
              />
            ) : (
              <TextField
                label={field.label}
                type={field.type}
                value={filterValues[field.key] || ""}
                onChange={e => onFilterChange(field.key, e.target.value)}
                fullWidth
                variant="outlined"
                style={SUPPORT_DEV_FILTER_CONFIG.styling.filterFieldStyle}
              />
            )}
          </Grid>
        ))}
      </Grid>
      <div style={{ marginTop: 24, textAlign: 'right' }}>
        <Button 
          color="secondary" 
          variant="outlined" 
          onClick={onClear} 
          style={{ minWidth: 120 }}
        >
          {SUPPORT_DEV_FILTER_CONFIG.filterDisplay.clearButtonText}
        </Button>
      </div>
    </CardContent>
  </Card>
);

// Dashboard Report Component
const DashboardReport = ({ reportData, fetchDashboardReport }) => {
  const { todayUpdated, totalPending, totalClosed, topCategories, topSubCategories, topByMappedIdCount, topModules, recentUpdatedTickets, totalMapped, totalMappedItems, averageItemsPerMapped, allTickets } = reportData;

  // Tab state
  const [tabIndex, setTabIndex] = useState(0);
  const handleTabChange = (event, newValue) => setTabIndex(newValue);

  // Filter state for dashboard tabs
  // Helper to get default 30-day window
  const getDefaultDateRange = () => {
    const today = new Date();
    const prior = new Date();
    prior.setDate(today.getDate() - 29); // 30 days including today
    return {
      from: prior.toISOString().split('T')[0],
      to: today.toISOString().split('T')[0],
    };
  };
  const defaultRange = getDefaultDateRange();
  const [fromDate, setFromDate] = useState(defaultRange.from);
  const [toDate, setToDate] = useState(defaultRange.to);

  // Pie chart data preparation
  const getPieData = (items) =>
    items.map((item) => ({ name: item.name, y: typeof item.count === 'number' ? item.count : 1 }));

  // Pie chart options
  const getPieOptions = (title, items) => ({
    chart: { type: 'pie', height: 300 },
    title: { text: title },
    tooltip: { pointFormat: '{series.name}: <b>{point.y}</b>' },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: { enabled: true, format: '<b>{point.name}</b>: {point.y}' },
      },
    },
    series: [
      {
        name: 'Count',
        colorByPoint: true,
        data: getPieData(items),
      },
    ],
    credits: { enabled: false },
  });

  // Tab content data
  const tabConfigs = [
    {
      label: 'Category Tickets',
      items: topCategories,
      pieTitle: 'Top Categories',
      raw: 'category', // for filtering
    },
    {
      label: 'Modules',
      items: topModules,
      pieTitle: 'Top Modules',
      raw: 'module', // for filtering
    },
    {
      label: 'Recent Updated',
      items: recentUpdatedTickets.map((item) => ({ ...item, count: 1 })), // Pie: count as 1 per ticket
      pieTitle: 'Recent Updated Tickets',
      listItems: recentUpdatedTickets, // For list, show original
      raw: 'recent', // for filtering
    },
    {
      label: 'COUNTS',
      // Grouped by type, then by developmentStatus
      items: (() => {
        const tickets = reportData && reportData.tickets ? reportData.tickets : [];
        if (!tickets.length) return [];
        // Group by type, then by developmentStatus
        const typeMap = {};
        tickets.forEach(ticket => {
          const type = ticket.type || 'Unknown';
          const status = ticket.developmentStatus || 'Unknown';
          if (!typeMap[type]) typeMap[type] = {};
          typeMap[type][status] = (typeMap[type][status] || 0) + 1;
        });
        // Convert to array: [{ type, statuses: [{ name, count }] }]
        return Object.entries(typeMap).map(([type, statusesObj]) => ({
          type,
          statuses: Object.entries(statusesObj).map(([name, count]) => ({ name, count }))
        }));
      })(),
      // For pie chart: counts by type
      pieData: (() => {
        const tickets = reportData && reportData.tickets ? reportData.tickets : [];
        if (!tickets.length) return [];
        const typeCounts = {};
        tickets.forEach(ticket => {
          const type = ticket.type || 'Unknown';
          typeCounts[type] = (typeCounts[type] || 0) + 1;
        });
        return Object.entries(typeCounts).map(([name, count]) => ({ name, y: count }));
      })(),
      pieTitle: 'Ticket Counts by Type',
    },
  ];

  // Helper to get all tickets for filtering (from reportData)
  const getAllTickets = () => {
    // We don't have all tickets here, but we can reconstruct from the top lists for demo purposes
    // In a real scenario, you should pass all tickets to DashboardReport
    return [];
  };

  // Filtering logic for each tab
  const filterTabItems = (tabIdx) => {
    // COUNTS tab: return grouped items as-is
    if (tabIdx === 3) return tabConfigs[tabIdx].items;
    let items = tabConfigs[tabIdx].listItems || tabConfigs[tabIdx].items;
    if (!items || items.length === 0) return [];
    // Only apply date filter for Recent Updated tab
    if (tabIdx === 2 && (fromDate || toDate)) {
      // recentUpdatedTickets: item.count is a date string
      return items.filter(item => {
        let pass = true;
        if (fromDate) {
          const itemDate = new Date(item.count);
          if (isNaN(itemDate) || itemDate < new Date(fromDate)) pass = false;
        }
        if (toDate) {
          const itemDate = new Date(item.count);
          if (isNaN(itemDate) || itemDate > new Date(toDate)) pass = false;
        }
        return pass;
      });
    }
    // For categories/modules, ignore date filter
    return items;
  };

  const StatCard = ({ title, value, icon, color, subtitle }) => (
    <Card style={{ 
      height: '100%', 
      boxShadow: '0 4px 12px rgba(33, 150, 243, 0.1)',
      border: `1px solid ${color}20`,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(33, 150, 243, 0.15)'
      }
    }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h4" style={{ fontWeight: 700, color: color || '#1976d2', marginBottom: 4 }}>
              {value}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 2, fontWeight: 500 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="textSecondary" style={{ fontStyle: 'italic' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar style={{ 
            backgroundColor: color || '#1976d2', 
            width: 56, 
            height: 56,
            boxShadow: `0 4px 8px ${color}40`
          }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  const TopListCard = ({ title, items, icon, color }) => {
    // Check if this is the recent tickets card (contains date strings)
    const isRecentTickets = title.includes("Recent Updated");
    
    return (
      <Card style={{ 
        height: '100%', 
        boxShadow: '0 4px 12px rgba(33, 150, 243, 0.1)',
        border: `1px solid ${color}20`
      }}>
        <CardContent>
          <Box display="flex" alignItems="center" marginBottom={3}>
            <Avatar style={{ 
              backgroundColor: color || '#1976d2', 
              width: 40, 
              height: 40, 
              marginRight: 12,
              boxShadow: `0 2px 6px ${color}40`
            }}>
              {icon}
            </Avatar>
            <Typography variant="h6" style={{ fontWeight: 600, color: '#1976d2' }}>
              {title}
            </Typography>
          </Box>
          <Box>
            {items.map((item, index) => (
              <Box 
                key={index} 
                display="flex" 
                justifyContent="space-between" 
                alignItems="center" 
                marginBottom={2}
                padding={1}
                style={{
                  backgroundColor: index % 2 === 0 ? '#f8f9fa' : 'transparent',
                  borderRadius: 4,
                  transition: 'background-color 0.2s ease'
                }}
              >
                <Typography variant="body2" style={{ flex: 1, fontWeight: 500 }}>
                  {item.name}
                </Typography>
                <Chip 
                  label={item.count} 
                  size="small" 
                  style={{ 
                    backgroundColor: isRecentTickets ? '#e91e63' : (color || '#1976d2'), 
                    color: 'white',
                    fontWeight: 600,
                    minWidth: isRecentTickets ? 80 : 40,
                    fontSize: isRecentTickets ? '0.7rem' : '0.75rem'
                  }}
                />
              </Box>
            ))}
            {items.length === 0 && (
              <Box 
                display="flex" 
                alignItems="center" 
                justifyContent="center" 
                padding={3}
                style={{ backgroundColor: '#f8f9fa', borderRadius: 4 }}
              >
                <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
                  No data available
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box marginBottom={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
        <Typography variant="h5" style={{ fontWeight: 600, color: '#1976d2' }}>
          Dashboard Report
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={fetchDashboardReport}
          style={{ minWidth: 100 }}
        >
          Refresh
        </Button>
      </Box>
      {/* Statistics Cards */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Today Updated"
            value={todayUpdated}
            icon={<TrendingUp />}
            color="#4caf50"
            subtitle="Tickets updated today"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Pending"
            value={totalPending}
            icon={""}
            color="#ff9800"
            subtitle="Tickets pending"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Closed"
            value={totalClosed}
            icon={<CheckCircle />}
            color="#4caf50"
            subtitle="Tickets resolved"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Mapped"
            value={totalMapped}
            icon={<Map />}
            color="#2196f3"
            subtitle={`${totalMappedItems} items (avg: ${averageItemsPerMapped.toFixed(1)})`}
          />
        </Grid>
      </Grid>
      {/* Tabs for Top Listings and Pie Charts */}
      <Paper style={{ marginBottom: 24 }}>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          {tabConfigs.map((tab, idx) => (
            <Tab key={tab.label} label={tab.label} />
          ))}
        </Tabs>
        <Box p={3}>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              {tabIndex === 3 ? (
                // COUNTS tab: grouped by type, then by status
                <Box>
                  <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 8 }}>Ticket Counts by Type & Status</Typography>
                  {filterTabItems(tabIndex).length === 0 && (
                    <Typography variant="body2" color="textSecondary">No data available</Typography>
                  )}
                  {filterTabItems(tabIndex).map(typeGroup => (
                    <Box key={typeGroup.type} mb={2} >
                      <Typography variant="subtitle1" style={{ fontWeight: 500, color: '#1976d2' }}>{typeGroup.type}</Typography>
                      <Box ml={2} style={{display:"flex"}}>
                        {typeGroup.statuses.map(status => (
                          <Box key={status.name}  display="flex" alignItems="center" mb={0.5}>
                            <Chip label={status.name + "-"+status.count} size="small" style={{ marginRight: 8 }} />
                           
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <TopListCard
                  title={tabConfigs[tabIndex].pieTitle}
                  items={filterTabItems(tabIndex)}
                  icon={
                    tabIndex === 0 ? <Category /> :
                    tabIndex === 1 ? <Apps /> :
                    tabIndex === 2 ? <Update /> :
                    <List />
                  }
                  color={
                    tabIndex === 0 ? "#9c27b0" :
                    tabIndex === 1 ? "#009688" :
                    tabIndex === 2 ? "#e91e63" :
                    "#607d8b"
                  }
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <HighchartsReact
                highcharts={Highcharts}
                options={
                  tabIndex === 3
                    ? {
                        chart: { type: 'pie', height: 300 },
                        title: { text: tabConfigs[tabIndex].pieTitle },
                        tooltip: { pointFormat: '{series.name}: <b>{point.y}</b>' },
                        plotOptions: {
                          pie: {
                            allowPointSelect: true,
                            cursor: 'pointer',
                            dataLabels: { enabled: true, format: '<b>{point.name}</b>: {point.y}' },
                          },
                        },
                        series: [
                          {
                            name: 'Count',
                            colorByPoint: true,
                            data: tabConfigs[tabIndex].pieData,
                          },
                        ],
                        credits: { enabled: false },
                      }
                    : getPieOptions(tabConfigs[tabIndex].pieTitle, filterTabItems(tabIndex))
                }
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>
      {/* Old Top Lists (commented out, replaced by tabs) */}
      {/*
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={12} md={6}>
          <TopListCard
            title="Top 5 Categories"
            items={topCategories}
            icon={<Category />}
            color="#9c27b0"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TopListCard
            title="Top 5 by Mapped Ticket Count"
            items={topByMappedIdCount}
            icon={<Timeline />}
            color="#3f51b5"
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={12} md={6}>
          <TopListCard
            title="Top 5 Modules"
            items={topModules}
            icon={<Apps />}
            color="#009688"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TopListCard
            title="Top 5 Recent Updated Tickets"
            items={recentUpdatedTickets}
            icon={<Update />}
            color="#e91e63"
          />
        </Grid>
      </Grid>
      */}
    </Box>
  );
};

const SupportDevPage = (props) => {
  const [fields, setFields] = useState([]);
  const [filterFields, setFilterFields] = useState([]);
  const [filterValues, setFilterValues] = useState(getDefaultFilterValues());
  const [records, setRecords] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', color: 'success' });
  const [editRow, setEditRow] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [configData, setConfigData] = useState({});
  const [mappingPopupOpen, setMappingPopupOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState('main'); // 'main', 'create', 'edit', 'config'
  const [reportData, setReportData] = useState({
    todayUpdated: 0,
    totalPending: 0,
    totalClosed: 0,
    totalMapped: 0,
    totalMappedItems: 0,
    averageItemsPerMapped: 0,
    topCategories: [],
    topSubCategories: [],
    topByMappedIdCount: [],
    topModules: [],
    recentUpdatedTickets: [],
    tickets: [] // Added tickets to reportData
  });
  const [reportLoading, setReportLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null);

  // TODO: Replace with actual user context or props
  const user = props.user;

  useEffect(() => {
    // Fetch field config from DB
    const fetchConfig = async () => {
      setConfigLoading(true);
      try {
        const res = await API_POST("action/getdata", { root: "systemSettingsConfiguration", con: { context: "supportDevConfig" } });
        if (res && res.data) {
          const configData = {
            types: [],
            modules: [],
            categories: [],
            subcategories: []
          };
          
          res.data.forEach(item => {
            if (item.name && item.value) {
              configData[item.name] = item.value;
            }
          });
          
          // Store config data for later use
          setConfigData(configData);
        } else {
          setConfigData({});
        }
      } catch (err) {
        console.error("Error loading config:", err);
        setConfigData({});
      } finally {
        setConfigLoading(false);
      }
    };

    const fetchTeamMembers = async () => {
      try {
        const teamData = getTeamDetails();
        console.log("teamData",teamData);
        if (teamData && typeof teamData === 'object') {
          // Extract usernames from the team data object
          const members = Object.keys(teamData).map(email => {
            const member = teamData[email];
            if(member['is_active'] == 0){
              return null;
            }
            return email;
          }).filter(e=>e);
          console.log("members",members); 
          setTeamMembers(members);
        } else {
         // setTeamMembers(["Mohit", "Shubendu", "Rahul"]); // Fallback to default values
        }
      } catch (err) {
        console.error("Error loading team members:", err);
      //  setTeamMembers(["Mohit", "Shubendu", "Rahul"]); // Fallback to default values
      }
    };

    const initializeData = async () => {
      await fetchTeamMembers();
      await fetchConfig();
      await fetchDashboardReport();
      getRecords();
    };

    initializeData();
  }, []);

  // Regenerate fields when teamMembers or configData changes
  useEffect(() => {
    if (teamMembers.length > 0 || Object.keys(configData).length > 0) {
      const dynamicFields = generateDynamicFields(configData, teamMembers);
      setFields(dynamicFields);
      
      // Use the filter configuration to set filter fields
      const configuredFilterFields = getOrderedFilterableFields().map(filterField => {
        // Find the corresponding field in dynamic fields to get options
        const dynamicField = dynamicFields.find(field => field.key === filterField.key);
        return {
          ...filterField,
          options: dynamicField ? dynamicField.options : []
        };
      });
      setFilterFields(configuredFilterFields);
    }
  }, [teamMembers, configData]);

  // Function to generate dynamic fields based on configuration
  const generateDynamicFields = (configData, teamMembers = []) => {
    const baseFields = [

      {
        key: "priority",
        label: "Priority",
        type: "select",
        options: [
          "Minus P1",
          "P0",
          "P1",
          "P2",
          "P3"
        ]
      },
      {
        key: "developmentStatus",
        label: "Development Status",
        type: "select",
        options: [
          "In Progress",
          "In Roadmap",
          "Under Development",
          "Under QA Testing",
          "QA Tested",
          "Move For Deployment",
          "Not Found",
          "Resolved",
          "Reopen"
        ]
      },
      { key: "assignTo", label: "Assign To", type: "select", options: teamMembers },
      { key: "asanaLink", label: "Asana Link", type: "text" },
      { key: "estimationTime", label: "Estimation Time", type: "text" },
      { key: "deployedDate", label: "Deployed Date", type: "date" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "comment", label: "Comment", type: "textarea" },
      { key: "admin", label: "Admin", type: "text", readonly: true },
      { key: "username", label: "Username", type: "text", readonly: true },
      { key: "createdAt", label: "Created At", type: "text", readonly: true },
      { key: "updatedAt", label: "Updated At", type: "text", readonly: true }
    ];

    // Add dynamic fields based on configuration
    const dynamicFields = [...baseFields];

    // Add type field if configured
    if (configData.types && configData.types.length > 0) {
      dynamicFields.splice(1, 0, {
        key: "type",
        label: "Type",
        type: "select",
        options: configData.types
      });
    }

    // Add module field if configured
    if (configData.modules && configData.modules.length > 0) {
      dynamicFields.splice(2, 0, {
        key: "module",
        label: "Module",
        type: "select",
        options: configData.modules
      });
    }

    // Add category field if configured
    if (configData.categories && configData.categories.length > 0) {
      dynamicFields.splice(3, 0, {
        key: "category",
        label: "Category",
        type: "select",
        options: configData.categories
      });
    }

    // Add subcategory field if configured
    if (configData.subcategories && configData.subcategories.length > 0) {
      dynamicFields.splice(4, 0, {
        key: "subCategory",
        label: "SubCategory",
        type: "select",
        options: configData.subcategories
      });
    }

    return dynamicFields;
  };

  // Function to fetch dashboard report data
  const fetchDashboardReport = async () => {
    setReportLoading(true);
    try {
      // Get today's date in YYYY-MM-DD format
      const today = new Date().toISOString().split('T')[0];
      
      // Fetch all tickets for analysis
      const allTicketsRes = await API_POST("action/getdata", { 
        root: "supportDevTickets", 
        con: {admin: user.email} 
      });
      
      if (allTicketsRes && allTicketsRes.data) {
        const tickets = allTicketsRes.data;
        
        // Calculate today's updated tickets
        const todayUpdated = tickets.filter(ticket => {
          const updatedAt = ticket.updatedAt || ticket.createdAt;
          if (updatedAt) {
            const ticketDate = new Date(updatedAt).toISOString().split('T')[0];
            return ticketDate === today;
          }
          return false;
        }).length;

        // Calculate pending tickets
        const totalPending = tickets.filter(ticket => 
          ticket.ticketStatus === "Pending (By Default)" || 
          ticket.ticketStatus === "Followups"
        ).length;

        // Calculate closed tickets
        const totalClosed = tickets.filter(ticket => 
          ticket.developmentStatus === "Resolved"
        ).length;

        // Calculate mapped tickets (tickets with category and subcategory)
        const totalMapped = tickets.filter(ticket => 
          ticket.mappedId && ticket.mappedId.length
        ).length;

        // Calculate total mapped items
        const totalMappedItems = tickets.reduce((total, ticket) => total + (ticket.mappedId ? ticket.mappedId.length : 0), 0);

        // Calculate average items per mapped ticket
        const averageItemsPerMapped = totalMappedItems / totalMapped;

        // Calculate top 5 categories
        const categoryCounts = {};
        tickets.forEach(ticket => {
          if (ticket.category) {
            categoryCounts[ticket.category] = (categoryCounts[ticket.category] || 0) + 1;
          }
        });
        const topCategories = Object.entries(categoryCounts)
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);

        // Calculate top 5 subcategories
        const subCategoryCounts = {};
        tickets.forEach(ticket => {
          if (ticket.subCategory) {
            subCategoryCounts[ticket.subCategory] = (subCategoryCounts[ticket.subCategory] || 0) + 1;
          }
        });
        const topSubCategories = Object.entries(subCategoryCounts)
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);

        // Calculate top 5 by mappedId count
        const ticketsWithMappedId = tickets.filter(ticket => ticket.mappedId && ticket.mappedId.length > 0);
        const topByMappedIdCount = ticketsWithMappedId
          .map(ticket => ({
            name: `${ticket.module || ticket.category || 'Unknown'} - ${ticket.ticketStatus || 'N/A'}`,
            count: ticket.mappedId.length
          }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);

        // Calculate top 5 modules
        const moduleCounts = {};
        tickets.forEach(ticket => {
          if (ticket.module) {
            moduleCounts[ticket.module] = (moduleCounts[ticket.module] || 0) + 1;
          }
        });
        const topModules = Object.entries(moduleCounts)
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);

        // Calculate top 5 recent updated tickets
        const recentUpdatedTickets = tickets
          .filter(ticket => ticket.updatedAt || ticket.createdAt)
          .map(ticket => ({
            name: `${ticket.module || ticket.category || 'Unknown'} - ${ticket.ticketStatus || 'N/A'}`,
            count: new Date(ticket.updatedAt || ticket.createdAt).toLocaleDateString()
          }))
          .sort((a, b) => new Date(b.count) - new Date(a.count))
          .slice(0, 5);

        setReportData({
          todayUpdated,
          totalPending,
          totalClosed,
          totalMapped,
          totalMappedItems,
          averageItemsPerMapped,
          topCategories,
          topSubCategories,
          topByMappedIdCount,
          topModules,
          recentUpdatedTickets,
          tickets: tickets // Store all tickets for the counts tab
        });
      }
    } catch (err) {
      console.error("Error fetching dashboard report:", err);
      setSnackbar({ open: true, message: 'Error loading dashboard report', color: 'error' });
    } finally {
      setReportLoading(false);
    }
  };

  const getRecords = async (filterData = {admin: user.email}) => {
    const res = await API_POST("action/getdata", { 
      root: "supportDevTickets", 
      con: filterData,
    });
    if (res && res.data && res.data ) {
      setRecords(res.data || []);
    }
    // Refresh dashboard report when records are updated
    await fetchDashboardReport();
  };

  const handleFilterChange = (fieldKey, value) => {
    const newFilterValues = { ...filterValues, [fieldKey]: value };
    setFilterValues(newFilterValues);
    
    // Check if auto-apply is enabled in configuration
    if (SUPPORT_DEV_FILTER_CONFIG.filterDisplay.autoApply) {
      // Remove empty values from filter
      const nonEmptyFilters = Object.fromEntries(
        Object.entries(newFilterValues).filter(([key, val]) => val && val !== "")
      );
      
      // Send API request with filter data
      getRecords(nonEmptyFilters);
    }
  };

  const handleClearFilter = () => {
    setFilterValues(getDefaultFilterValues());
    getRecords(); // Get all records without filters
  };

  const handleEdit = (row) => {
    setEditRow(row);
    setCurrentPage('edit');
  };

  const handleCreate = () => {
    setEditRow(null);
    setCurrentPage('create');
  };

  const handleConfigBack = () => {
    setCurrentPage('main');
    // Reload the page to get updated configuration
    window.location.reload();
  };

  const handleBackToMain = () => {
    setCurrentPage('main');
    setEditRow(null);
  };

  const handleSuccess = () => {
    getRecords();
    fetchDashboardReport();
    setCurrentPage('main');
  };

  const handleDelete = (row) => {
    setTicketToDelete(row);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!ticketToDelete) return;
    try {
      await API_POST("action/deleteProspects", {
        root: "supportDevTickets",
        querydata: { _id: ticketToDelete._id },
      });
      setSnackbar({ open: true, message: 'Ticket deleted successfully', color: 'success' });
      setDeleteDialogOpen(false);
      setTicketToDelete(null);
      getRecords();
    } catch (err) {
      setSnackbar({ open: true, message: 'Error deleting ticket', color: 'error' });
      setDeleteDialogOpen(false);
      setTicketToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setTicketToDelete(null);
  };

  // Explicitly define which fields should be visible in the table
  const visibleFieldKeys = [
    "createdAt",
    "ticketStatus",
    "priority",
    "developmentStatus",
    "assignTo",
    "estimationTime",
    "deployedDate",
    "type",
    "module",
    "category",
    "subCategory",

  ];
  const showFields = (fields || []).filter(field => visibleFieldKeys.includes(field.key));

  // Table columns from showFields
  const columns = [
    ...showFields.map(field => ({
      name: field.label,
      selector: field.key,
      sortable: true,
      ...( ["category","developmentStatus","subCategory"].includes(field.key) && {width: "300px"}),
      cell: field.key === "createdAt" ? row => {
        const date = row[field.key];
        return date ? new Date(date).toLocaleDateString() + ' ' + new Date(date).toLocaleTimeString() : '';
      } : undefined
    })),
    {
      name: "Mapped",
      selector: row => {
        const hasMapping = row.mappedId && row.mappedId.length > 0;
        return hasMapping ? `Mapped (${row.mappedId.length})` : "Not Mapped";
      },
      sortable: true,
      cell: row => {
        const isMapped = row.mappedId && row.mappedId.length > 0;
        const mappedCount = row.mappedId ? row.mappedId.length : 0;
        return (
          <Box display="flex" alignItems="center">
            <Chip
              label={isMapped ? `Mapped (${mappedCount})` : "Not Mapped"}
              size="small"
              style={{
                backgroundColor: isMapped ? '#4caf50' : '#f44336',
                color: 'white',
                fontWeight: 500
              }}
            />
            {isMapped && (
              <Tooltip title={`${mappedCount} item${mappedCount !== 1 ? 's' : ''}`}>
                <Typography variant="caption" style={{ marginLeft: 8, color: '#666' }}>
               
                </Typography>
              </Tooltip>
            )}
          </Box>
        );
      },
      width: '150px'
    },
    {
      name: "Actions",
      cell: row => (
        <>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<EditAttributesOutlined />}
            onClick={() => handleEdit(row)}
            style={{ marginRight: 8 }}
          >
            Edit
          </Button>
        </>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    }
  ];

  const SupportDevTable = ({ data }) => (
    <DataTable
      columns={columns}
      data={data}
      pagination
      highlightOnHover
    />
  );

  const filteredRecords = records; // No longer filtering locally

  const handleExportExcel = async () => {
    try {
      // Filter non-empty values
      const filteredSettings = {};

      for (const [key, value] of Object.entries(filterValues)) {
        if (value !== "") {
          filteredSettings[key] = value;
        }
      }
      const downloadFile = await API_POST("action/ExportExcel", {
        querydata: filteredSettings,
        body: {
          admin: user.admin,
          page: "supportDevTickets",
        },
      });
      if (downloadFile.status === 200) {
        setSnackbar({
          open: true,
          message: "File is in process, Please check in system log section.",
          color: "success",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error exporting to Excel.",
        color: "error",
      });
      console.error("Error exporting to Excel:", error);
    }
  };

  if (currentPage === 'config') {
    return <SupportDevConfigPage onBack={handleConfigBack} />;
  }

  return (
    <div style={{  margin: '0 auto', padding: 24 }}>
      {configLoading ? (
        <div style={{ textAlign: 'center', margin: 40 }}>
          <CircularProgress />
          <Typography variant="subtitle1" style={{ marginTop: 16 }}>Loading configuration...</Typography>
        </div>
      ) : (
        <>
          {currentPage === 'create' && (
            <CreateTicketPage
              onBack={handleBackToMain}
              onSuccess={handleSuccess}
              user={user}
              configData={configData}
              teamMembers={teamMembers}
            />
          )}
          {currentPage === 'edit' && (
            <EditTicketPage
              onBack={handleBackToMain}
              onSuccess={handleSuccess}
              user={user}
              configData={configData}
              teamMembers={teamMembers}
              editData={editRow}
            />
          )}
          {currentPage === 'main' && (
            <>
              {/* Dashboard Report */}
              <DashboardReport reportData={reportData} fetchDashboardReport={fetchDashboardReport} />
              <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" style={{ fontWeight: 600, color: '#1976d2' }}>
                  Support Dev Tickets
                </Typography>
                <div>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCreate}
                    style={{ fontWeight: 500, marginRight: 12 }}
                  >
                    Create New Ticket
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleExportExcel}
                    title="Export To Excel"
                    startIcon={<GetAppOutlinedIcon />}
                    style={{ fontWeight: 500 }}
                  >
                    Export To Excel
                  </Button>
                </div>
              </div>
              <SupportDevFilter
                filterFields={filterFields}
                filterValues={filterValues}
                setFilterValues={setFilterValues}
                onClear={handleClearFilter}
                onFilterChange={handleFilterChange}
              />
              <SupportDevTable data={filteredRecords} />
            </>
          )}
        </>
      )}
      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        ContentProps={{ style: { backgroundColor: snackbar.color === 'success' ? '#43a047' : '#d32f2f', color: '#fff' } }}
      />
      
      <SupportDevMappingPopup
        open={mappingPopupOpen}
        onClose={() => setMappingPopupOpen(false)}
        onSuccess={() => {
          getRecords();
          setSnackbar({ open: true, message: 'Tickets updated successfully', color: 'success' });
        }}
        user={user}
      />
      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Paper style={{ padding: 32, minWidth: 320, textAlign: 'center' }}>
            <Typography variant="h6" style={{ marginBottom: 16 }}>Delete Ticket</Typography>
            <Typography variant="body1" style={{ marginBottom: 24 }}>
              Are you sure you want to delete this ticket? This action cannot be undone.
            </Typography>
            <Box display="flex" justifyContent="center" gap={2}>
              <Button onClick={handleCancelDelete} variant="outlined" color="default" style={{ marginRight: 16 }}>Cancel</Button>
              <Button onClick={handleConfirmDelete} variant="contained" color="secondary">Delete</Button>
            </Box>
          </Paper>
        </div>
      )}
    </div>
  );
};

export default SupportDevPage; 